<script setup>
import { ref, provide, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import CloseButton from '@/components/CloseButton.vue';

// Vue twin of <x-pop-out-panel> (resources/views/components/pop-out-panel.blade.php).
// Same elements and class hooks, no styling of its own - both sides are styled by
// resources/css/frontend/includes/components/pop-out-panel.scss, which is what stops
// them drifting apart. See docs/blade-to-vue-components.md.
//
// Deliberately carries no data-pop-out-panel-id or data-pop-out-panel-overlay. Those
// attributes are how resources/js/app.js finds the panels it owns, and app.js ignores
// any panel without one, so the two systems share the class hooks for styling without
// either touching the other's DOM. Do not add them for parity's sake.
//
// Rendered by PopOutPanelHost.vue, one per entry in shared/panelStack.js.

const props = defineProps({
    title:      { type: String,  default: null },
    titleClass: { type: String,  default: null },

    // Another panel is open on top of this one. The panel stays mounted and keeps
    // its state, and .covered hides it (pop-out-panel.scss), so closing the panel
    // above reveals it exactly as it was left.
    covered:    { type: Boolean, default: false },

    // The stack has asked this panel to close. Drops .visible to run the slide-out,
    // then emits `closed` so the owner can unmount it.
    closing:    { type: Boolean, default: false },

    // Bumped whenever this panel's contents are replaced in place
    // (replacePanelContent in shared/panelStack.js). The panel is the scrolling
    // element, not the content, so without this it would keep the outgoing content's
    // scroll position - swap a scrolled calendar for a short basket and you land
    // part-way down an empty panel.
    contentVersion: { type: Number, default: 0 },

    // Whether this panel's overlay should be the one dimming the page. Every panel
    // renders an overlay for markup parity with Blade, but only the bottom of the stack
    // ever shows one: they are identical and cover the same area, so a second fading in
    // over the first buys nothing and, because the overlay's fade is sequenced to wait
    // out a panel slide, reads as the dimming flashing off and back on.
    showOverlay: { type: Boolean, default: true },
});

const emit = defineEmits(['close', 'opened', 'closed']);

// The Blade view has two root elements (the panel and its overlay), so Vue's
// single-root attribute fallthrough does not apply - caller classes are bound to
// the panel explicitly instead.
defineOptions({ inheritAttrs: false });

const panel = ref(null);
const body = ref(null);
const footer = ref(null);
const visible = ref(false);

// Descendants teleport their footer content in here rather than passing it up
// through the body slot, since the content that belongs in a footer (a total, a
// Continue button) usually depends on state the content component owns. Each panel
// provides its own element, so nearest-ancestor resolution puts content in its own
// panel's footer however deep the stack is.
provide('popOutPanelFooter', footer);

// Both ends of the slide are reported to the owner, because both matter to the stack:
// `opened` is when this panel is genuinely covering the one below (so that one may be
// hidden), and `closed` is when it has finished sliding away (so it may be unmounted).
// One handler for both directions, keyed off the current state.
//
// Each has a timeout fallback, since transitionend never fires if the transition does
// not run at all - transitions disabled, or the element not painted. Without them a
// panel could stay in the stack forever, or never let the one below hide.
let openFallback = null;
let closeFallback = null;

function onTransitionEnd(event) {
    if (event.target !== panel.value || event.propertyName !== 'transform') {
        return;
    }

    visible.value ? announceOpened() : announceClosed();
}

function announceOpened() {
    clearTimeout(openFallback);
    emit('opened');
}

function announceClosed() {
    clearTimeout(closeFallback);
    emit('closed');
}

onMounted(() => {
    panel.value?.addEventListener('transitionend', onTransitionEnd);

    // .visible cannot go on in the same frame the element mounts, or there is no
    // starting position to animate from. Two frames rather than one: a single rAF can
    // still be batched into the same style recalculation as the insertion.
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            visible.value = true;
        });
    });

    openFallback = setTimeout(announceOpened, 1000);

    nextTick(observeFooter);
});

// Removing .visible runs the slide-out. The panel must not be unmounted until that
// finishes, so the owner is told separately, on transitionend.
watch(() => props.closing, (closing) => {
    if (closing) {
        visible.value = false;
        closeFallback = setTimeout(announceClosed, 1000);
    }
});

watch(() => props.contentVersion, () => {
    if (panel.value) {
        panel.value.scrollTop = 0;
    }
});

// Toggles .stuck on the footer while it is pinned to the panel's bottom edge, so its
// solid fill only appears when content is passing beneath it. Same measurement as
// app.js's observePopOutPanelFooter() - CSS cannot detect this, there is no :stuck
// pseudo-class - but scoped to this instance and torn down on unmount, which the
// global sweep has no way to do.
let scheduled = false;

function updateStuck() {
    scheduled = false;

    if (!footer.value || !panel.value) {
        return;
    }

    // 1px of tolerance for sub-pixel layout, which otherwise flickers the class on
    // and off at the boundary.
    const stuck = footer.value.getBoundingClientRect().bottom
        >= panel.value.getBoundingClientRect().bottom - 1;

    footer.value.classList.toggle('stuck', stuck);
}

// Coalesced to one measurement per frame - scroll fires far more often than the
// layout can meaningfully change.
function scheduleStuck() {
    if (!scheduled) {
        scheduled = true;
        requestAnimationFrame(updateStuck);
    }
}

// A ResizeObserver rather than a window resize listener, because the measurement has
// to survive the panel's content changing height, not just the viewport changing size.
// Content arrives asynchronously here (the calendar fetches its dates after mount) and
// is swapped wholesale by replacePanelContent(), and neither fires scroll or resize -
// so a listener-only version measures once against an empty panel and never corrects
// itself. Observing the panel covers viewport height changes too, since the panel is
// full height, which is what the window listener was there for.
let resizeObserver = null;

function observeFooter() {
    panel.value?.addEventListener('scroll', scheduleStuck);

    resizeObserver = new ResizeObserver(scheduleStuck);
    if (panel.value) resizeObserver.observe(panel.value);
    if (body.value) resizeObserver.observe(body.value);

    updateStuck();
}

onBeforeUnmount(() => {
    panel.value?.removeEventListener('scroll', scheduleStuck);
    panel.value?.removeEventListener('transitionend', onTransitionEnd);
    resizeObserver?.disconnect();
    clearTimeout(openFallback);
    clearTimeout(closeFallback);
});
</script>

<template>
    <nav
        ref="panel"
        class="pop_out_panel"
        :class="{ visible, covered }"
        v-bind="$attrs"
    >
        <div class="pop_out_panel_header">
            <h4 v-if="title">
                <span :class="titleClass">{{ title }}</span>
            </h4>
            <CloseButton
                class="pop_out_panel_close"
                aria-label="Close menu"
                @click="emit('close')"
            />
        </div>
        <div ref="body" class="pop_out_panel_body">
            <slot />
        </div>
        <!-- Always rendered, unlike Blade's @if(!empty($footer)) - it has to exist for
             descendants to teleport into (see the provide above). pop-out-panel.scss
             hides it with :empty while nothing has landed in it, so an unused footer
             costs no padding. -->
        <div ref="footer" class="pop_out_panel_footer">
            <slot name="footer" />
        </div>
    </nav>
    <aside
        class="pop_out_panel_overlay"
        :class="{ visible: visible && showOverlay }"
        aria-hidden="true"
        @click="emit('close')"
    ></aside>
</template>
